/**
 * Date-only arithmetic for the clinical-study calendar (#893). Every date this calendar shows is a
 * `YYYY-MM-DD` day without a time (target dates, windows, appointments, treatment dates, pool
 * dates), so nothing here converts between time zones: the days are computed on the UTC calendar,
 * where a date-only string means exactly the day it names (ui-ux-rules U7: a date without a time
 * is never presented as an instant to convert). Only «oggi» depends on a zone, and it is read in
 * Europe/Rome, the zone the console's profile fixes for every organisation.
 */

export const CALENDAR_TIME_ZONE = 'Europe/Rome';

/** A `YYYY-MM` month key. */
export type MonthKey = string;

function pad(value: number): string {
  return String(value).padStart(2, '0');
}

function toUtcDate(date: string): Date {
  const [year, month, day] = date.split('-').map(Number);
  return new Date(Date.UTC(year ?? 1970, (month ?? 1) - 1, day ?? 1));
}

function fromUtcDate(value: Date): string {
  return `${value.getUTCFullYear()}-${pad(value.getUTCMonth() + 1)}-${pad(value.getUTCDate())}`;
}

/** Today in Europe/Rome as `YYYY-MM-DD`, whatever zone the browser runs in. */
export function todayInRome(now: Date = new Date()): string {
  // `en-CA` formats as YYYY-MM-DD.
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: CALENDAR_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(now);
}

export function addDays(date: string, days: number): string {
  const value = toUtcDate(date);
  value.setUTCDate(value.getUTCDate() + days);
  return fromUtcDate(value);
}

export function monthOf(date: string): MonthKey {
  return date.slice(0, 7);
}

export function addMonths(month: MonthKey, months: number): MonthKey {
  const value = toUtcDate(`${month}-01`);
  value.setUTCMonth(value.getUTCMonth() + months);
  return fromUtcDate(value).slice(0, 7);
}

/** Days from `start` to `end`, both included; empty when `end` precedes `start`. */
export function daysBetween(start: string, end: string): string[] {
  const days: string[] = [];
  for (let day = start; day <= end; day = addDays(day, 1)) {
    days.push(day);
  }
  return days;
}

/** Every day of `month`, first to last. */
export function daysOfMonth(month: MonthKey): string[] {
  const first = `${month}-01`;
  const last = addDays(`${addMonths(month, 1)}-01`, -1);
  return daysBetween(first, last);
}

/** `dd/mm/yyyy` — the console's date format (ui-product-profile § 1, U7). */
export function formatDay(date: string): string {
  const [year, month, day] = date.split('-');
  return `${day}/${month}/${year}`;
}

/** «novembre 2026» / «November 2026». */
export function formatMonthTitle(month: MonthKey, locale: string): string {
  return new Intl.DateTimeFormat(locale, {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(toUtcDate(`${month}-01`));
}

/** «lunedì 3 novembre 2026», the accessible name of a day. */
export function formatLongDay(date: string, locale: string): string {
  return new Intl.DateTimeFormat(locale, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(toUtcDate(date));
}
